import React, { useContext, useState, useEffect } from "react";
import PatientDetails from "../../Components/PatientInformation";
import SaveAndNavigate from "../../Components/SaveAndNavigate";
import SideBarMenu from "../../Components/SideBarMenu";
import ErrorField from "../../Components/ErrorField";
import { AppContext } from "../../Contexts/AppContextProvider";
import DateControl from "../../Components/DateControl";
import { DefaultDate, HTTPResponse, SideMenuItems, ToastMessageType, ToastMessages } from "../../Helpers/Enums";
import { GetDisplayDate, GetDMUID, GetLocalStorageData, GetLoggedInUserID, IsDateEmpty, ParseDate, SetDateState, isStringEmpty, getTodayFormatted } from "../../Helpers/Utilities";
import { CommonError_Msgs, SelectedDiseaseAndTreatment_Msgs } from "../../Helpers/HelperText";
import { SelectedDiseaseAndTreatmentService } from "../../WebApiServices/SelectedDiseaseAndTreatment.service";

const SelectedDiseaseAndTreatment = () => {
    let patientData = GetLocalStorageData("patient");
    patientData = patientData && JSON.parse(patientData);
    const pid = GetLocalStorageData("patientId") ?? 0;
    const userId = GetLoggedInUserID() ?? 0;

    const { ShowToast, ToggleLoader, HandleSessionTimeout } = useContext(AppContext);

    const navigatePaths = {
        prevPage: "/PatientScreening",
        currPage: "/SelectedDiseaseAndTreatment",
        nextPage: "/Demographics",
    };

    // ── States ───────────────────────────────────────────────────────────────────

    const initialDetails = {
        // R1
        data_refresh: null,             // 1 = Yes, 0 = No
        second_chart_abstract_date: DefaultDate,
        // R2
        tp53_tested: null, //1 = Yes, 0 = No, 99 = Unknown
        tp53_result: null,   // 1 = Mutated, 2 = Unmutated, 99 = Unknown
        hospice_setting: null,
        // Indicated symptoms (R5) — checkboxes
        indicated_rapidly_progressive: false,
        indicated_explosive: false,
        indicated_poor_prognosis: false,
        indicated_organ_compromise: false,
        indicated_rapid_rampup: false,
        // Indicated symptoms (R5) — exclusive option (mutually exclusive with the checkboxes above)
        indicated_exclusive: null,   // 0 = None documented, 99 = Unknown
        // Present symptoms (R6) — checkboxes
        present_ldh_elevated: false,
        present_bulky_mass: false,
        present_tumor_lysis: false,
        present_tumor_burden: false,
        present_ecog_deterioration: false,
        present_hospitalization: false,
        present_bridging_therapy: false,
        // Present symptoms (R6) — exclusive option
        present_exclusive: null,   // 0 = None documented, 99 = Unknown
        urgent_initiation: null,   // 1 = Yes inpatient, 2 = Yes outpatient expedited, 0 = No, 99 = Unknown
        // R8
        cd20_assess: null,        // 1 = date, 99 = Unknown, 2 = Not assessed
        cd20_assess_date: DefaultDate,
        hgbcl_diagnosed: null,
    };

    const [sdtDetails, setSdtDetails] = useState(initialDetails);
    const [indexDate, setIndexDate] = useState("[index_date]");

    const InitialHardErrList = {
        r1Empty: false,
        r2Empty: false,
        r3Empty: false,
        r4Empty: false,
        r5Empty: false,
        r6Empty: false,
        r7Empty: false,
        r8Empty: false,
        r8DateEmpty: false,
        r8DateInvalid: false,
        r8DateInvalidMsg: "",
        r9Empty: false,
    };

    const [hardErrList, setHardErrList] = useState(InitialHardErrList);
    const [showTopErrMsg, setShowTopErrMsg] = useState(false);

    useEffect(() => {
        async function FetchData() {
            if (!isNaN(pid) && Number(pid) !== 0) {
                ToggleLoader(true);
                await LoadData();
                ToggleLoader(false);
            }
        }
        FetchData();
    }, []);

    const LoadData = async () => {
        try {
            if (pid > 0) {
                const response = await SelectedDiseaseAndTreatmentService.GetSDTData(pid);
                if (response?.status === HTTPResponse.OK) {
                    const { data } = response;

                    try {
                        setSdtDetails((prev) => ({
                            ...prev,
                            data_refresh: data.dataRefresh === true ? 1
                                : data.dataRefresh === null || data.dataRefresh === undefined ? null
                                    : 0,
                            second_chart_abstract_date: data.dataRefresh && data.secondChartAbstractDate && !isStringEmpty(data.secondChartAbstractDate)
                                ? data.secondChartAbstractDate
                                : DefaultDate,
                            tp53_tested: data.tp53Tested ?? null,
                            tp53_result: data.tp53Result ?? null,
                            hospice_setting: data.hospiceSettingsDose ?? null,
                            indicated_rapidly_progressive: data.indicatedSymptoms_RapidlyProgressiveDisease ?? false,
                            indicated_explosive: data.indicatedSymptoms_ExplosiveDisease ?? false,
                            indicated_poor_prognosis: data.indicatedSymptoms_PoorPrognosis ?? false,
                            indicated_organ_compromise: data.indicatedSymptoms_OrganCompromise ?? false,
                            indicated_rapid_rampup: data.indicatedSymptoms_RapidRampup ?? false,
                            indicated_exclusive: data.indicatedSymptoms_NoneDocumented === 1 ? 0
                                : data.indicatedSymptoms_Unknown === 99 ? 99 : null,
                            present_ldh_elevated: data.presentSymptoms_Ldh ?? false,
                            present_bulky_mass: data.presentSymptoms_BulkyMass ?? false,
                            present_tumor_lysis: data.presentSymptoms_TumorLysis ?? false,
                            present_tumor_burden: data.presentSymptoms_TumorBurden ?? false,
                            present_ecog_deterioration: data.presentSymptoms_EcogDeterioration ?? false,
                            present_hospitalization: data.presentSymptoms_Hospitalization ?? false,
                            present_bridging_therapy: data.presentSymptoms_BridgingTherapy ?? false,
                            present_exclusive: data.presentSymptoms_NoneDocumented === 1 ? 0
                                : data.presentSymptoms_Unknown === 99 ? 99 : null,
                            urgent_initiation: data.urgentInitiation ?? null,
                            cd20_assess: data.cd20NotAssessed === 1 ? 2
                                : data.cd20AssessUnk === 99 ? 99
                                    : (data.cd20AssessDate && !isStringEmpty(data.cd20AssessDate)) ? 1 : null,
                            cd20_assess_date: (data.cd20AssessDate && !isStringEmpty(data.cd20AssessDate))
                                ? GetDisplayDate(data.cd20AssessDate, data.cd20AssessDateDmuid)
                                : DefaultDate,
                            hgbcl_diagnosed: data.hgbclDiagnosed ?? null,
                        }));
                        if (data.indexDate) {
                            setIndexDate(data.indexDate);
                        }
                    } catch (setStateError) {
                        console.error("setSdtDetails error:", setStateError);
                    }

                } else if (response?.status === HTTPResponse.Unauthorized) {
                    HandleSessionTimeout();
                } else {
                    console.error("Unexpected response status:", response?.status, response?.error);
                    throw response.error;
                }
            }
        } catch (error) {
            console.error("LoadData caught error:", error);
            ShowToast(CommonError_Msgs.LOAD_ERR, ToastMessageType.Failed);
        }
    };

    const HandleDateChange = (name, date, datePart) => {
        switch (name) {
            case "cd20_assess_date": {
                setSdtDetails((prev) => ({
                    ...prev,
                    cd20_assess: 1,
                    cd20_assess_date: SetDateState(prev.cd20_assess_date, date, datePart),
                }));
                setHardErrList((prev) => ({ ...prev, r8Empty: false, r8DateEmpty: false, r8DateInvalid: false }));
                break;
            }

            default:
                break;
        }
    };

    // ── Handlers ─────────────────────────────────────────────────────────────────

    const HandleFieldChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case "data_refresh": {
                const val = parseInt(value);
                setSdtDetails((prev) => ({
                    ...prev,
                    data_refresh: val,
                }));
                setHardErrList((prev) => ({ ...prev, r1Empty: false }));
                break;
            }
            case "tp53_tested": {
                const val = parseInt(value);
                setSdtDetails((prev) => ({
                    ...prev,
                    tp53_tested: val,
                    // clear R3 if user deselects Yes
                    tp53_result: val === 1 ? prev.tp53_result : null,
                }));
                setHardErrList((prev) => ({ ...prev, r2Empty: false, r3Empty: false }));
                break;
            }
            case "tp53_result": {
                setSdtDetails((prev) => ({ ...prev, tp53_result: parseInt(value) }));
                setHardErrList((prev) => ({ ...prev, r3Empty: false }));
                break;
            }
            case "hospice_setting": {
                setSdtDetails((prev) => ({ ...prev, hospice_setting: parseInt(value) }));
                setHardErrList((prev) => ({ ...prev, r4Empty: false }));
                break;
            }
            case "indicated_rapidly_progressive":
            case "indicated_explosive":
            case "indicated_poor_prognosis":
            case "indicated_organ_compromise":
            case "indicated_rapid_rampup": {
                const { checked } = e.target;
                setSdtDetails((prev) => ({
                    ...prev,
                    [name]: checked,
                    // any checkbox selection clears the exclusive choice
                    indicated_exclusive: checked ? null : prev.indicated_exclusive,
                }));
                setHardErrList((prev) => ({ ...prev, r5Empty: false }));
                break;
            }
            case "indicated_exclusive": {
                setSdtDetails((prev) => ({
                    ...prev,
                    indicated_exclusive: parseInt(value),
                    // selecting None/Unknown clears all checkboxes
                    indicated_rapidly_progressive: false,
                    indicated_explosive: false,
                    indicated_poor_prognosis: false,
                    indicated_organ_compromise: false,
                    indicated_rapid_rampup: false,
                }));
                setHardErrList((prev) => ({ ...prev, r5Empty: false }));
                break;
            }
            case "present_ldh_elevated":
            case "present_bulky_mass":
            case "present_tumor_lysis":
            case "present_tumor_burden":
            case "present_ecog_deterioration":
            case "present_hospitalization":
            case "present_bridging_therapy": {
                const { checked } = e.target;
                setSdtDetails((prev) => ({
                    ...prev,
                    [name]: checked,
                    present_exclusive: checked ? null : prev.present_exclusive,
                }));
                setHardErrList((prev) => ({ ...prev, r6Empty: false }));
                break;
            }
            case "present_exclusive": {
                setSdtDetails((prev) => ({
                    ...prev,
                    present_exclusive: parseInt(value),
                    present_ldh_elevated: false,
                    present_bulky_mass: false,
                    present_tumor_lysis: false,
                    present_tumor_burden: false,
                    present_ecog_deterioration: false,
                    present_hospitalization: false,
                    present_bridging_therapy: false,
                }));
                setHardErrList((prev) => ({ ...prev, r6Empty: false }));
                break;
            }
            case "urgent_initiation": {
                setSdtDetails((prev) => ({ ...prev, urgent_initiation: parseInt(value) }));
                setHardErrList((prev) => ({ ...prev, r7Empty: false }));
                break;
            }
            case "cd20_assess": {
                const val = parseInt(value);
                setSdtDetails((prev) => ({
                    ...prev,
                    cd20_assess: val,
                    cd20_assess_date: val === 1 ? prev.cd20_assess_date : DefaultDate,
                }));
                setHardErrList((prev) => ({ ...prev, r8Empty: false }));
                break;
            }
            case "hgbcl_diagnosed": {
                setSdtDetails((prev) => ({ ...prev, hgbcl_diagnosed: parseInt(value) }));
                setHardErrList((prev) => ({ ...prev, r9Empty: false }));
                break;
            }
            default:
                break;
        }
    };

    // ── Save ─────────────────────────────────────────────────────────────────────

    const ValidateForm = () => {
        let isValid = true;

        if (sdtDetails.data_refresh === null) {
            setHardErrList((prev) => ({ ...prev, r1Empty: true }));
            isValid = false;
        }
        if (sdtDetails.tp53_tested === null) {
            setHardErrList((prev) => ({ ...prev, r2Empty: true }));
            isValid = false;
        }
        if (sdtDetails.tp53_tested === 1 && sdtDetails.tp53_result === null) {
            setHardErrList((prev) => ({ ...prev, r3Empty: true }));
            isValid = false;
        }
        if (sdtDetails.hospice_setting === null) {
            setHardErrList((prev) => ({ ...prev, r4Empty: true }));
            isValid = false;
        }
        const r5AnyChecked = sdtDetails.indicated_rapidly_progressive || sdtDetails.indicated_explosive ||
            sdtDetails.indicated_poor_prognosis || sdtDetails.indicated_organ_compromise || sdtDetails.indicated_rapid_rampup;
        if (!r5AnyChecked && sdtDetails.indicated_exclusive === null) {
            setHardErrList((prev) => ({ ...prev, r5Empty: true }));
            isValid = false;
        }
        const r6AnyChecked = sdtDetails.present_ldh_elevated || sdtDetails.present_bulky_mass ||
            sdtDetails.present_tumor_lysis || sdtDetails.present_tumor_burden || sdtDetails.present_ecog_deterioration ||
            sdtDetails.present_hospitalization || sdtDetails.present_bridging_therapy;
        if (!r6AnyChecked && sdtDetails.present_exclusive === null) {
            setHardErrList((prev) => ({ ...prev, r6Empty: true }));
            isValid = false;
        }
        if (sdtDetails.urgent_initiation === null) {
            setHardErrList((prev) => ({ ...prev, r7Empty: true }));
            isValid = false;
        }
        if (sdtDetails.cd20_assess === null) {
            setHardErrList((prev) => ({ ...prev, r8Empty: true }));
            isValid = false;
        }

        if (sdtDetails.cd20_assess === 1 && IsDateEmpty(sdtDetails.cd20_assess_date)) {
            setHardErrList((prev) => ({ ...prev, r8DateEmpty: true }));
            isValid = false;
        }

        if (sdtDetails.cd20_assess === 1 && !IsDateEmpty(sdtDetails.cd20_assess_date)) {
            const assessed = new Date(ParseDate(sdtDetails.cd20_assess_date));
            const index = new Date(ParseDate(indexDate));
            const indexMinus12 = new Date(index);
            indexMinus12.setFullYear(indexMinus12.getFullYear() - 1);

            const parts = sdtDetails.cd20_assess_date.split("/");
            const monthIsUnk = parts[0] === "UNK";
            const dayIsUnk = parts[1] === "UNK";

            if (
                !monthIsUnk &&
                !dayIsUnk &&
                (assessed < indexMinus12 || assessed > index)
            ) {
                const fmt = (d) => `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
                setHardErrList((prev) => ({
                    ...prev,
                    r8DateInvalid: true,
                    r8DateInvalidMsg: `Date must be between ${fmt(indexMinus12)} and ${fmt(index)}.`,
                }));
                isValid = false;
            }
        }
        if (sdtDetails.hgbcl_diagnosed === null) {
            setHardErrList((prev) => ({ ...prev, r9Empty: true }));
            isValid = false;
        }

        return isValid;
    };

    const SavePage = async (validate) => {
        try {
            ToggleLoader(true);

            if (validate) {
                setHardErrList(InitialHardErrList);
                setShowTopErrMsg(false);

                if (!ValidateForm()) {
                    setShowTopErrMsg(true);
                    ToggleLoader(false);
                    return false;
                }
            }

            const payload = {
                patientId: pid,
                userId: userId,
                createdBy: userId,
                editedBy: userId,
                isValidated: validate,

                // R1
                dataRefresh: sdtDetails.data_refresh === null ? null : sdtDetails.data_refresh === 1,
                secondChartAbstractDate: sdtDetails.data_refresh === 1 && !IsDateEmpty(sdtDetails.second_chart_abstract_date)
                    ? sdtDetails.second_chart_abstract_date
                    : null,

                // R2
                tp53Tested: sdtDetails.tp53_tested,

                // R3 — only if R2 = Yes
                tp53Result: sdtDetails.tp53_tested === 1
                    ? sdtDetails.tp53_result
                    : null,

                // R4
                hospiceSettingsDose: sdtDetails.hospice_setting,

                // R5
                indicatedSymptoms_RapidlyProgressiveDisease: sdtDetails.indicated_rapidly_progressive,
                indicatedSymptoms_ExplosiveDisease: sdtDetails.indicated_explosive,
                indicatedSymptoms_PoorPrognosis: sdtDetails.indicated_poor_prognosis,
                indicatedSymptoms_OrganCompromise: sdtDetails.indicated_organ_compromise,
                indicatedSymptoms_RapidRampup: sdtDetails.indicated_rapid_rampup,
                indicatedSymptoms_NoneDocumented: sdtDetails.indicated_exclusive === 0 ? 1 : null,
                indicatedSymptoms_Unknown: sdtDetails.indicated_exclusive === 99 ? 99 : null,

                // R6
                presentSymptoms_Ldh: sdtDetails.present_ldh_elevated,
                presentSymptoms_BulkyMass: sdtDetails.present_bulky_mass,
                presentSymptoms_TumorLysis: sdtDetails.present_tumor_lysis,
                presentSymptoms_TumorBurden: sdtDetails.present_tumor_burden,
                presentSymptoms_EcogDeterioration: sdtDetails.present_ecog_deterioration,
                presentSymptoms_Hospitalization: sdtDetails.present_hospitalization,
                presentSymptoms_BridgingTherapy: sdtDetails.present_bridging_therapy,
                presentSymptoms_NoneDocumented: sdtDetails.present_exclusive === 0 ? 1 : null,
                presentSymptoms_Unknown: sdtDetails.present_exclusive === 99 ? 99 : null,

                // R7
                urgentInitiation: sdtDetails.urgent_initiation,

                // R8
                cd20AssessDate: sdtDetails.cd20_assess === 1 && !IsDateEmpty(sdtDetails.cd20_assess_date)
                    ? ParseDate(sdtDetails.cd20_assess_date)
                    : null,
                cd20AssessDateDmuid: sdtDetails.cd20_assess === 1
                    ? GetDMUID(sdtDetails.cd20_assess_date)
                    : null,
                cd20AssessUnk: sdtDetails.cd20_assess === 99 ? 99 : null,
                cd20NotAssessed: sdtDetails.cd20_assess === 2 ? 1 : null,

                // R9
                hgbclDiagnosed: sdtDetails.hgbcl_diagnosed,
            };

            const response = await SelectedDiseaseAndTreatmentService.SaveSDTData(payload);

            ToggleLoader(false);

            if (response?.status !== HTTPResponse.OK) {
                if (response?.status === HTTPResponse.Unauthorized)
                    HandleSessionTimeout();
                else
                    throw response.error;
            } else {
                setShowTopErrMsg(false);
                setHardErrList(InitialHardErrList); 
                await LoadData();
                ShowToast(ToastMessages.Save_Success, ToastMessageType.Success);
                return true;
            }
        } catch (error) {
            ToggleLoader(false);
            ShowToast(CommonError_Msgs.SAVE_ERR, ToastMessageType.Failed);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <section className="survey-patient-details-section">
                <div className="row">
                    <PatientDetails />
                    <div className="col-12 col-sm-5 col-md-5 col-lg-6 col-content-alignment">
                        <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
                    </div>
                </div>
            </section>

            <section className="main-survey-section">
                <div className="row">
                    <SideBarMenu activePageId={SideMenuItems.SelectedDiseaseAndTreatment} />

                    <div className="col-md-9 col-pad-left">
                        <div className="survey-section-content">

                            <div className="survey-section-content-header">
                                <h4>SELECTED DISEASE AND TREATMENT CHARACTERISTICS</h4>
                            </div>

                            {showTopErrMsg && (
                                <div className="clean-error">
                                    <ErrorField show={showTopErrMsg} message={SelectedDiseaseAndTreatment_Msgs.showTopErrMsg} />
                                </div>
                            )}


                            <div className="survey-question-section">
                                <div className="survey-question-content">
                                    <div className="question-answer">

                                        {/* ── R1 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R1.</span>
                                                <span className="quest-text-pad">
                                                    Are you revising the information in this patient chart
                                                    as part of a data refresh?
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="data_refresh"
                                                            value={1}
                                                            checked={sdtDetails.data_refresh === 1}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Yes</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="data_refresh"
                                                            value={0}
                                                            checked={sdtDetails.data_refresh === 0}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">No</span>
                                                    </label>
                                                </div>

                                                {sdtDetails.data_refresh === 1 && (
                                                    <> Date of second chart abstraction initiation:&nbsp;
                                                        <b className="index-date">
                                                            {!IsDateEmpty(sdtDetails.second_chart_abstract_date)
                                                                ? sdtDetails.second_chart_abstract_date
                                                                : ""}
                                                        </b>
                                                    </>
                                                )}
                                            </div>

                                            <ErrorField show={hardErrList.r1Empty} message={SelectedDiseaseAndTreatment_Msgs.r1Empty} />
                                        </div>

                                        {/* ── R2 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R2.</span>
                                                <span className="quest-text-pad">
                                                    Was TP53 mutation testing performed prior to{" "}
                                                    <span className="index-date">{indexDate}</span>, the date of initiation of
                                                    treatment with epcoritamab in the real world?
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="tp53_tested"
                                                            value={1}
                                                            checked={sdtDetails.tp53_tested === 1}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Yes</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="tp53_tested"
                                                            value={2}
                                                            checked={sdtDetails.tp53_tested === 2}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">No</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="tp53_tested"
                                                            value={99}
                                                            checked={sdtDetails.tp53_tested === 99}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <ErrorField show={hardErrList.r2Empty} message={SelectedDiseaseAndTreatment_Msgs.r2Empty} />
                                        </div>

                                        {/* ── R3 (conditional on R2 = Yes) ────────────────────── */}
                                        {sdtDetails.tp53_tested === 1 && (
                                            <div className="question-bot-sapce">
                                                <div className="question question-weight">
                                                    <span>R3.</span>
                                                    <span className="quest-text-pad">
                                                        What was the result of the TP53 mutation testing
                                                        prior to <span className="index-date">{indexDate}</span>, the date of initiation
                                                        of treatment with epcoritamab in the real world?
                                                    </span>
                                                </div>

                                                <div className="double-dig-answer">
                                                    <div className="">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                name="tp53_result"
                                                                value={1}
                                                                checked={sdtDetails.tp53_result === 1}
                                                                onChange={HandleFieldChange}
                                                            />
                                                            <span className="radio-text-padding">Mutated</span>
                                                        </label>
                                                    </div>

                                                    <div className="">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                name="tp53_result"
                                                                value={2}
                                                                checked={sdtDetails.tp53_result === 2}
                                                                onChange={HandleFieldChange}
                                                            />
                                                            <span className="radio-text-padding">Unmutated</span>
                                                        </label>
                                                    </div>

                                                    <div className="">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                name="tp53_result"
                                                                value={99}
                                                                checked={sdtDetails.tp53_result === 99}
                                                                onChange={HandleFieldChange}
                                                            />
                                                            <span className="radio-text-padding">Unknown</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <ErrorField show={hardErrList.r3Empty} message={SelectedDiseaseAndTreatment_Msgs.r3Empty} />
                                            </div>
                                        )}

                                        {/* ── R4 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R4.</span>
                                                <span className="quest-text-pad">
                                                    Did the patient receive the first dose of epcoritamab
                                                    on <span className="index-date">{indexDate}</span>, the date of initiation of
                                                    treatment with epcoritamab in the real world, in a
                                                    hospice setting?
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="hospice_setting"
                                                            value={1}
                                                            checked={sdtDetails.hospice_setting === 1}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Yes</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="hospice_setting"
                                                            value={2}
                                                            checked={sdtDetails.hospice_setting === 2}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">No</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="hospice_setting"
                                                            value={99}
                                                            checked={sdtDetails.hospice_setting === 99}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <ErrorField show={hardErrList.r4Empty} message={SelectedDiseaseAndTreatment_Msgs.r4Empty} />
                                        </div>

                                        {/* ── R5 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R5.</span>
                                                <span className="quest-text-pad">
                                                    Within 30 days prior to <span className="index-date">{indexDate}</span>, the date of
                                                    initiation of treatment with epcoritamab in the real
                                                    world, did documentation indicate any of the following?
                                                    (select all that apply)
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="indicated_rapidly_progressive"
                                                            checked={sdtDetails.indicated_rapidly_progressive}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Rapidly progressive disease</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="indicated_explosive"
                                                            checked={sdtDetails.indicated_explosive}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Explosive or hyperproliferative disease</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="indicated_poor_prognosis"
                                                            checked={sdtDetails.indicated_poor_prognosis}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Very poor prognosis / limited life expectancy</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                        <input
                                                            type="checkbox"
                                                            name="indicated_organ_compromise"
                                                            checked={sdtDetails.indicated_organ_compromise}
                                                            onChange={HandleFieldChange}
                                                            style={{ flexShrink: 0, marginTop: "3px" }}
                                                        />
                                                        <span className="radio-text-padding">Impending organ compromise (e.g., airway, spinal cord, liver failure)</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="indicated_rapid_rampup"
                                                            checked={sdtDetails.indicated_rapid_rampup}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Need for rapid ramp up of treatment</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="indicated_exclusive"
                                                            value={0}
                                                            checked={sdtDetails.indicated_exclusive === 0}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">None documented</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="indicated_exclusive"
                                                            value={99}
                                                            checked={sdtDetails.indicated_exclusive === 99}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <ErrorField show={hardErrList.r5Empty} message={SelectedDiseaseAndTreatment_Msgs.r5Empty} />
                                        </div>

                                        {/* ── R6 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R6.</span>
                                                <span className="quest-text-pad">
                                                    Within 30 days prior to <span className="index-date">{indexDate}</span>, the date of
                                                    initiation of treatment with epcoritamab in the real
                                                    world, were any of the following present? (select all that apply)
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                        <input
                                                            type="checkbox"
                                                            name="present_ldh_elevated"
                                                            checked={sdtDetails.present_ldh_elevated}
                                                            onChange={HandleFieldChange}
                                                            style={{ flexShrink: 0, marginTop: "3px" }}
                                                        />
                                                        <span className="radio-text-padding">
                                                            Lactate dehydrogenase (LDH) &ge; 2&times; upper limit of normal
                                                        </span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                        <input
                                                            type="checkbox"
                                                            name="present_bulky_mass"
                                                            checked={sdtDetails.present_bulky_mass}
                                                            onChange={HandleFieldChange}
                                                            style={{ flexShrink: 0, marginTop: "3px" }}
                                                        />
                                                        <span className="radio-text-padding">
                                                            New or rapidly enlarging bulky mass (&gt;10 cm or doubling in size)
                                                        </span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="present_tumor_lysis"
                                                            checked={sdtDetails.present_tumor_lysis}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Symptomatic tumor lysis syndrome</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="present_tumor_burden"
                                                            checked={sdtDetails.present_tumor_burden}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Rapid increase in total tumor burden on imaging</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="present_ecog_deterioration"
                                                            checked={sdtDetails.present_ecog_deterioration}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">ECOG deterioration attributable to lymphoma</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="present_hospitalization"
                                                            checked={sdtDetails.present_hospitalization}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Hospitalization due to lymphoma progression</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            name="present_bridging_therapy"
                                                            checked={sdtDetails.present_bridging_therapy}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Requirement for immediate bridging therapy</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="present_exclusive"
                                                            value={0}
                                                            checked={sdtDetails.present_exclusive === 0}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">None documented</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="present_exclusive"
                                                            value={99}
                                                            checked={sdtDetails.present_exclusive === 99}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <ErrorField show={hardErrList.r6Empty} message={SelectedDiseaseAndTreatment_Msgs.r6Empty} />
                                        </div>

                                        {/* ── R7 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R7.</span>
                                                <span className="quest-text-pad">
                                                    Was epcoritamab initiated in the real world urgently due
                                                    to concern for rapid disease progression?
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                        <input
                                                            type="radio"
                                                            name="urgent_initiation"
                                                            value={1}
                                                            checked={sdtDetails.urgent_initiation === 1}
                                                            onChange={HandleFieldChange}
                                                            style={{ flexShrink: 0, marginTop: "3px" }}
                                                        />
                                                        <span className="radio-text-padding">
                                                            Yes, patient initiated treatment with epcoritamab during inpatient hospitalization
                                                        </span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label style={{ display: "flex", alignItems: "flex-start" }}>
                                                        <input
                                                            type="radio"
                                                            name="urgent_initiation"
                                                            value={2}
                                                            checked={sdtDetails.urgent_initiation === 2}
                                                            onChange={HandleFieldChange}
                                                            style={{ flexShrink: 0, marginTop: "3px" }}
                                                        />
                                                        <span className="radio-text-padding">
                                                            Yes, patient expedited treatment with epcoritamab in an outpatient setting (e.g., &lt;7 days from treatment decision)
                                                        </span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="urgent_initiation"
                                                            value={3}
                                                            checked={sdtDetails.urgent_initiation === 3}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">No</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="urgent_initiation"
                                                            value={99}
                                                            checked={sdtDetails.urgent_initiation === 99}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <ErrorField show={hardErrList.r7Empty} message={SelectedDiseaseAndTreatment_Msgs.r7Empty} />
                                        </div>

                                        {/* ── R8 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R8.</span>
                                                <span className="quest-text-pad">
                                                    What was the date of the patient's CD20 status assessment closest to (within 12 months prior to) or on{" "}
                                                    <span className="index-date">{indexDate}</span>, the date of initiation of treatment with epcoritamab in the real world?
                                                </span>
                                            </div>

                                            <div className="double-dig-answer">
                                                <div className="date-field-padding" style={{ display: "flex", alignItems: "flex-start" }}>
                                                    <input
                                                        type="radio"
                                                        name="cd20_assess"
                                                        value={1}
                                                        checked={sdtDetails.cd20_assess === 1}
                                                        onChange={HandleFieldChange}
                                                    />
                                                    <span className="radio-text-padding">
                                                        <DateControl
                                                            ctrlId={"cd20_assess_date"}
                                                            HandleDateChange={HandleDateChange}
                                                            date={sdtDetails.cd20_assess_date}
                                                            showUnkInfoTxt={true}
                                                        />
                                                    </span>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="cd20_assess"
                                                            value={99}
                                                            checked={sdtDetails.cd20_assess === 99}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>

                                                <div className="">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name="cd20_assess"
                                                            value={2}
                                                            checked={sdtDetails.cd20_assess === 2}
                                                            onChange={HandleFieldChange}
                                                        />
                                                        <span className="radio-text-padding">Not assessed</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <ErrorField show={hardErrList.r8Empty} message={SelectedDiseaseAndTreatment_Msgs.r8Empty} />
                                            <ErrorField show={hardErrList.r8DateEmpty} message={SelectedDiseaseAndTreatment_Msgs.r8DateEmpty} />
                                            <ErrorField show={hardErrList.r8DateInvalid} message={hardErrList.r8DateInvalidMsg} />
                                        </div>

                                        {/* ── R9 ───────────────────────────────────────────────── */}
                                        <div className="question-bot-sapce">
                                            <div className="question question-weight">
                                                <span>R9.</span>
                                                <span className="quest-text-pad">
                                                    Was the patient diagnosed with high-grade B-cell lymphoma
                                                    (HGBCL) prior to initiation of epcoritamab on{" "}
                                                    <span className="index-date">{indexDate}</span>?
                                                </span>
                                            </div>
                                            <div className="double-dig-answer">
                                                <div className="">
                                                    <label>
                                                        <input type="radio" name="hgbcl_diagnosed" value={1} checked={sdtDetails.hgbcl_diagnosed === 1} onChange={HandleFieldChange} />
                                                        <span className="radio-text-padding">Yes</span>
                                                    </label>
                                                </div>
                                                <div className="">
                                                    <label>
                                                        <input type="radio" name="hgbcl_diagnosed" value={2} checked={sdtDetails.hgbcl_diagnosed === 2} onChange={HandleFieldChange} />
                                                        <span className="radio-text-padding">No</span>
                                                    </label>
                                                </div>
                                                <div className="">
                                                    <label>
                                                        <input type="radio" name="hgbcl_diagnosed" value={99} checked={sdtDetails.hgbcl_diagnosed === 99} onChange={HandleFieldChange} />
                                                        <span className="radio-text-padding">Unknown</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <ErrorField show={hardErrList.r9Empty} message={SelectedDiseaseAndTreatment_Msgs.r9Empty} />
                                        </div>

                                    </div>
                                </div>
                            </div>
                            <div className="survey-section-content-foot">
                                <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default SelectedDiseaseAndTreatment;